import { createSelector } from "@reduxjs/toolkit";
import { isLoyaltyQuoteCurrent } from "../../../utils/cart/quoteFreshness";
import type { RootState } from "../app/store";
import { initialLoyaltyCartState } from "../types/LoyaltyCartTypes";

export const selectLoyaltyCart = (state: RootState) =>
  state.cartLoyaltyV2Reducer.loyalty ?? initialLoyaltyCartState;

export const selectLoyaltyCartLines = (state: RootState) =>
  selectLoyaltyCart(state).lines;

export const selectLoyaltyCartResult = (state: RootState) =>
  selectLoyaltyCart(state).result;

export const selectLoyaltyRedemption = (state: RootState) =>
  selectLoyaltyCart(state).redemption;

export const selectLoyaltyApplyFlow = (state: RootState) =>
  selectLoyaltyCart(state).applyFlow;

export const selectCustomerLoyaltyOffersRaw = (state: RootState) =>
  selectLoyaltyCart(state).customerOffers;

export const selectRedeemedLoyaltyOfferRaw = (state: RootState) =>
  selectLoyaltyRedemption(state)?.offer;

export const selectRedeemedOfferApplicationId = (state: RootState) =>
  selectLoyaltyRedemption(state)?.offerApplicationId;

export const selectLoyaltyAddedItemLineIds = (state: RootState) =>
  selectLoyaltyRedemption(state)?.addedLineIds;

export const selectSelectedLoyaltyOfferId = (state: RootState) =>
  selectLoyaltyApplyFlow(state)?.selectedOfferId;

/** Sub-item uuids of the cart lines pushed by the active redemption. */
export const selectRedeemedLineSubItemIds = createSelector(
  selectLoyaltyCartLines,
  selectLoyaltyAddedItemLineIds,
  (lines, addedLineIds): string[] => {
    if (!addedLineIds?.length) {
      return [];
    }

    const addedIds = new Set(addedLineIds);

    return lines
      .filter((line) => line.lineId && addedIds.has(line.lineId))
      .map((line) => line.subItemId);
  }
);

/**
 * Sub-item uuids of the active redemption, once per unit: two of the same
 * drink are two entries, which is what the offer collage draws.
 */
export const selectRedeemedLineUnits = createSelector(
  selectLoyaltyCartLines,
  selectLoyaltyAddedItemLineIds,
  (lines, addedLineIds): string[] => {
    if (!addedLineIds?.length) {
      return [];
    }

    const addedIds = new Set(addedLineIds);

    return lines
      .filter((line) => line.lineId && addedIds.has(line.lineId))
      .flatMap((line) =>
        Array<string>(Math.max(line.quantity, 1)).fill(line.subItemId)
      );
  }
);

export const selectLoyaltyOfferError = (state: RootState) =>
  selectLoyaltyCart(state).offerError;

/** A calculate for this cart is in flight, so the stored quote may be replaced. */
export const selectIsLoyaltyCartRepricing = (state: RootState) =>
  (state.cartLoyaltyV2Reducer.pendingOperations ?? 0) > 0;

/** See `isLoyaltyQuoteCurrent`. */
export const selectIsLoyaltyQuoteCurrent = (state: RootState) =>
  isLoyaltyQuoteCurrent(
    selectLoyaltyCartResult(state),
    state.cartLoyaltyV2Reducer.latestCartVersion,
    selectLoyaltyCartLines(state)
  );
